import { useState, useMemo } from 'react';
import type { ReactNode } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { useSelector, useSetUsage, useSetUsageType } from '../context/hooks';
import { selectMeter, selectUsage, selectUsageType } from '../context/selectors';

import { ChoiceCard } from './choice-card';
import { StepNav } from './step-nav';
import { Stepper } from './stepper';
import { METER_LABELS } from './strings';

import type { BulletOrIconListItem } from './strings';
import type { UsageType } from '../context/types';

type NeedsEntry = {
	label: string;
	description: string | ReactNode;
	info?: string;
	list?: BulletOrIconListItem[];
};

export const NeedsStep = () => {
	const intl = useIntl();
	const usageType = useSelector(selectUsageType);
	const setUsageType = useSetUsageType();
	const meter = useSelector(selectMeter);
	const usage = useSelector(selectUsage);
	const setUsage = useSetUsage();
	const [usageInput, setUsageInput] = useState(String(usage));

	const NEEDS_STRINGS = useMemo<Record<UsageType, NeedsEntry>>(
		() => ({
			exploring: {
				label: 'Exploring',
				description: intl.formatMessage({
					id: 'ubp.calculator.needs.exploring.description',
					defaultMessage:
						'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Praesent vitae.',
				}),
				info: intl.formatMessage({
					id: 'ubp.calculator.needs.exploring.info',
					defaultMessage:
						'Teams in pilot typically save 2-3 hours per person per week before any broad rollout',
				}),
				list: [
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.needs.exploring.list.rovoChat',
							defaultMessage: '200 Rovo Chat conversations',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.needs.exploring.list.agentRuns',
							defaultMessage: '2-3 agent runs',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.needs.exploring.list.customAgents',
							defaultMessage: '1–2 custom agents',
						}),
					},
				],
			},
			adopting: {
				label: 'Adopting',
				description: intl.formatMessage({
					id: 'ubp.calculator.needs.adopting.description',
					defaultMessage:
						'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer nec odio.',
				}),
				info: intl.formatMessage({
					id: 'ubp.calculator.needs.adopting.info',
					defaultMessage:
						'Customers report up to 40% faster issue resolution when Rovo handles routine queries day-to-day.',
				}),
				list: [
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.needs.adopting.list.rovoChat',
							defaultMessage: '200 Rovo Chat conversations',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.needs.adopting.list.agentRuns',
							defaultMessage: '2-3 agent runs',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.needs.adopting.list.customAgents',
							defaultMessage: '1–2 custom agents',
						}),
					},
				],
			},
			scaling: {
				label: 'Scaling',
				description: intl.formatMessage({
					id: 'ubp.calculator.needs.scaling.description',
					defaultMessage:
						'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor.',
				}),
				info: intl.formatMessage({
					id: 'ubp.calculator.needs.scaling.info',
					defaultMessage:
						'Teams in pilot typically save 2-3 hours per person per week before any broad rollout',
				}),
				list: [
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.needs.scaling.list.rovoChat',
							defaultMessage: '200 Rovo Chat conversations',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.needs.scaling.list.agentRuns',
							defaultMessage: '2-3 agent runs',
						}),
					},
					{
						text: intl.formatMessage({
							id: 'ubp.calculator.needs.scaling.list.customAgents',
							defaultMessage: '1–2 custom agents',
						}),
					},
				],
			},
		}),
		[intl],
	);

	const needsTypes = Object.keys(NEEDS_STRINGS) as UsageType[];

	return (
		<TypographyStyles>
			<Stepper />
			<h3 css={pageTitleStyle}>
				{intl.formatMessage({
					id: 'ubp.calculator.needs.pageTitle',
					defaultMessage: 'Tell us about your team\u2019s usage',
				})}
			</h3>
			<p css={pageSubtitleStyle} id="needs-step-heading">
				{intl.formatMessage(
					{
						id: 'ubp.calculator.needs.pageSubtitle',
						defaultMessage:
							'Choose an option that best describes how your team will use {meterLabel} each month',
					},
					{ meterLabel: METER_LABELS[meter] },
				)}
			</p>
			<div css={cardGridStyle} role="radiogroup" aria-labelledby="needs-step-heading">
				{needsTypes.map((choice: UsageType) => {
					const { label, description, list, info } = NEEDS_STRINGS[choice];
					return (
						<ChoiceCard
							key={`needs-${choice}`}
							value={choice}
							selected={usageType === choice}
							onSelect={() => setUsageType(choice)}
							groupName="group-needs"
							label={label}
							description={description}
							list={list}
							info={info}
						/>
					);
				})}
			</div>
			<div css={usageQuestionStyle}>
				<h3 css={usageQuestionHeadingStyle} id="usage-label">
					<label htmlFor="usage">
						{intl.formatMessage({
							id: 'ubp.calculator.needs.usageQuestion',
							defaultMessage: 'Do you know your usage?',
						})}
					</label>
				</h3>
				<div css={inputAndOptionalStyle}>
					<input
						id="usage"
						type="number"
						css={numberInputStyle}
						value={usageInput}
						min={0}
						aria-labelledby="usage-label"
						placeholder={`Current ${METER_LABELS[meter]} usage`}
						onChange={(e) => {
							const next = e.target.value;
							setUsageInput(next);
							setUsage(next === '' ? 0 : Number(next));
						}}
					/>
					<span css={optionalLabelStyle}>
						{intl.formatMessage({
							id: 'ubp.calculator.needs.optional',
							defaultMessage: 'Optional',
						})}
					</span>
				</div>
			</div>
			<StepNav showBack showNext nextLabel="Get estimate" />
		</TypographyStyles>
	);
};

// Page title — rendered as an H3 (Charlie Display Bold 32/38 via
// TypographyStyles). Left-aligned per Figma; 40px top padding keeps the title
// spaced from the Stepper, and the subtitle below owns the bottom spacing so
// the title + subtitle stay visually grouped.
const pageTitleStyle = css({
	margin: 0,
	paddingBlockStart: '40px',
});

// Body Large subtitle beneath the H3 title. Charlie Text 20/28 with the
// standard neutral text color. Sits directly under the title with a small gap
// above (space.100) and 40px padding below to separate the title/subtitle
// pair from the choice-card grid. The visible copy dynamically references the
// meter chosen in the previous step (e.g. "Rovo Credits", "Automations", "CS
// Resolutions", "Assets Objects") via ICU `{meterLabel}` interpolation.
const pageSubtitleStyle = css({
	margin: 0,
	paddingBlockStart: token('space.100'),
	paddingBlockEnd: '40px',
	fontSize: '1.25rem',
	lineHeight: '1.75rem',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
});

const cardGridStyle = css({
	display: 'grid',
	gap: token('space.400'),
	gridTemplateColumns: '1fr 1fr 1fr',
	[media.below.md]: {
		gridTemplateColumns: '1fr 1fr',
	},
	[media.below.sm]: {
		gridTemplateColumns: '1fr',
	},
});

// Wraps the usage-question H3 and the input + "Optional" indicator so they sit
// on the same row — mirrors usage-step.tsx's orgSizeQuestionStyle so both steps
// share the same input row treatment. 60px top padding lives on the row
// wrapper so both sides shift together and stay vertically aligned.
const usageQuestionStyle = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: token('space.200'),
	flexWrap: 'wrap',
	paddingBlockStart: '60px',
});

const usageQuestionHeadingStyle = css({
	margin: 0,
});

// Places the number input and the "Optional" indicator side-by-side, with the
// label vertically centered on the input.
const inputAndOptionalStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
});

const numberInputStyle = css({
	width: 160,
	height: 40,
	paddingBlock: token('space.100'),
	paddingInline: token('space.200'),
	fontSize: '1rem',
	lineHeight: '24px',
	border: `1px solid ${wacToken('color.neutral.light600')}`,
	borderRadius: 20,
	backgroundColor: wacToken('color.neutral.light0'),
	color: wacToken('color.neutral.light1100'),
});

const optionalLabelStyle = css({
	fontSize: '0.875rem',
	lineHeight: '20px',
	color: wacToken('color.neutral.light1100'),
});
